// Delivery estimate on product pages. The product component writes the current
// selection to data-weight (grams), data-total (pence), data-available and
// data-in-stock; this element adds what is already in the basket and looks the
// charge up in the rates from Theme settings > Delivery estimate.
if (!customElements.get('eg-delivery-estimate')) {
  customElements.define('eg-delivery-estimate', class extends HTMLElement {
    static get observedAttributes() { return ['data-weight', 'data-total', 'data-available', 'data-in-stock', 'data-more-hint']; }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.rates = (this.dataset.rates || '').split(';').map((line) => {
        const match = line.match(/^\s*([\d.]+)\s*=\s*([\d.]+)\s*(pallet)?/i);
        return match && { maxGrams: Number(match[1]) * 1000, price: Math.round(Number(match[2]) * 100), pallet: Boolean(match[3]) };
      }).filter(Boolean).sort((a, b) => a.maxGrams - b.maxGrams);
      this.freeFrom = Number(this.dataset.freeFrom) || 0;
      document.addEventListener('product-ajax:added', () => this.refreshBasket(), options);
      document.addEventListener('cart:updated', event => this.refreshBasket(event.detail?.cart), options);
      ['cart-loaded', 'cart-updated'].forEach(type => window.addEventListener(`aftersell-upcart:public-events:${type}`, event => this.refreshBasket(event.detail?.cart), options));
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    attributeChangedCallback() {
      if (this.controller) this.render();
    }

    get basket() {
      return { weight: Number(this.dataset.basketWeight) || 0, total: Number(this.dataset.basketTotal) || 0 };
    }

    async refreshBasket(cart) {
      const revision = this.basketRevision = (this.basketRevision || 0) + 1;
      try {
        if (!Number.isFinite(cart?.total_weight) || !Number.isFinite(cart?.total_price)) {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
          if (!response.ok) return;
          cart = await response.json();
        }
        if (revision !== this.basketRevision || !this.controller) return;
        this.dataset.basketWeight = String(cart.total_weight);
        this.dataset.basketTotal = String(cart.total_price);
        this.render();
        this.dispatchEvent(new CustomEvent('delivery-estimate:basket', { bubbles: true }));
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Delivery estimate could not read the basket.', error);
      }
    }

    money(pence) {
      return new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' }).format(pence / 100);
    }

    render() {
      const live = this.querySelector('[data-delivery-live]');
      const fallback = this.querySelector('[data-delivery-fallback]');
      if (!live) return;
      const weight = Number(this.dataset.weight), total = Number(this.dataset.total);
      const ready = this.rates.length > 0 && this.dataset.available === 'true' && Number.isFinite(weight) && total > 0;
      live.hidden = !ready;
      if (fallback) fallback.hidden = ready;
      if (!ready) { delete this.dataset.state; return; }

      const basket = this.basket;
      const orderWeight = basket.weight + weight, orderTotal = basket.total + total;
      const free = this.freeFrom > 0 && orderTotal >= this.freeFrom;
      const rate = this.rates.find(candidate => orderWeight <= candidate.maxGrams) || this.rates[this.rates.length - 1];
      this.dataset.state = free ? 'free' : 'paid';
      this.querySelector('[data-delivery-cost]').textContent = free
        ? this.dataset.labelFree
        : this.dataset[basket.total > 0 ? 'labelCostBasket' : 'labelCost'].replace('[price]', this.money(rate.price));
      const detail = this.querySelector('[data-delivery-detail]');
      if (detail) detail.hidden = !rate.pallet;
      const more = this.querySelector('[data-delivery-more]');
      if (more) {
        more.hidden = free || !this.freeFrom;
        more.textContent = more.hidden ? '' : this.dataset.moreHint || this.dataset.labelMore.replace('[amount]', this.money(this.freeFrom - orderTotal));
      }
      // Timing is only promised for stock on hand.
      const kind = rate.pallet ? 'pallet' : 'parcel';
      this.querySelectorAll('[data-delivery-timing]').forEach((note) => { note.hidden = this.dataset.inStock !== 'true' || note.dataset.deliveryTiming !== kind; });
    }
  });
}
